import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, CheckCircle, PlayCircle, Wand2, Info } from 'lucide-react';
import { BinSizeDataTable } from './BinSizeDataTable';
import { PriorityDataTable } from './PriorityDataTable';
import { MovementTypeDataTable } from './MovementTypeDataTable';
import { BinGrpDataTable } from './BinGrpDataTable';
import { ResourceDataTable } from './ResourceDataTable';
import { SortingBoxDefDataTable } from './SortingBoxDefDataTable';
import { ZoneDataTable } from './ZoneDataTable';
import { BinTypeDataTable } from './BinTypeDataTable';

import { MovementCodePriorityDataTable } from './MovementCodePriorityDataTable';
import { BinGrpRelationDataTable } from './BinGrpRelationDataTable';
import { StcResourceDto } from '../../../../../services/stcResourceService';
import { StcZoneDto } from '../../../../../services/stcZoneService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface WizardStep {
    id: string;
    title: string;
    description: string;
    instructions: string;
}

interface SelectedRows {
    [key: string]: boolean;
}

interface MasterDataWizardProps {
    onClose: () => void;
}

export const MasterDataWizard: React.FC<MasterDataWizardProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const [showInstructions, setShowInstructions] = useState<boolean>(false);
    
    // Data states for each step
    const [resourceData, setResourceData] = useState<StcResourceDto[]>([]);
    const [zoneData, setZoneData] = useState<StcZoneDto[]>([]);

    const wizardSteps: WizardStep[] = [
        // 'bin-category'-steget fjernet 2026-07-06: lokasjonskategorier er felles kodeverk i
        // master-StatusCodes (StatusType 42) og settes ikke lenger opp per tenant.
        {
            id: 'bin-size',
            title: t('manager.modules.adminResources.wizard.binSize.title', {}, 'Lokasjonsstørrelser'),
            description: t('manager.modules.adminResources.wizard.binSize.description', {}, 'Konfigurer størrelser for lagerplasser'),
            instructions: t('manager.modules.adminResources.wizard.binSize.instructions', {}, 'Definer de fysiske dimensjonene til lagerplassene. Oppgi bredde, dybde og høyde for hver størrelse. Systemet vil automatisk beregne totalt volum. Dette brukes for å matche varer med passende lagerplasser og for å optimalisere plassering basert på varestørrelse.')
        },
        {
            id: 'priority',
            title: t('manager.modules.adminResources.wizard.priority.title', {}, 'Plasseringsprioritet'),
            description: t('manager.modules.adminResources.wizard.priority.description', {}, 'Sett prioritering for plassering av varer'),
            instructions: t('manager.modules.adminResources.wizard.priority.instructions', {}, 'Opprett prioritetsnivåer som styrer hvor varer skal plasseres først. For eksempel kan prioritet 1 være for høyt omsatte varer som skal stå nærmest pakkestasjonene, mens prioritet 5 kan være for lavt omsatte varer som kan stå lengre unna. Dette optimaliserer plukketider og arbeidsflyt.')
        },
        {
            id: 'movement-type',
            title: t('manager.modules.adminResources.wizard.movementType.title', {}, 'Bevegelseskoder'),
            description: t('manager.modules.adminResources.wizard.movementType.description', {}, 'Definer typer varebevegelser i lageret'),
            instructions: t('manager.modules.adminResources.wizard.movementType.instructions', {}, 'Opprett bevegelseskoder som beskriver forskjellige typer varebevegelser i lageret. Typiske eksempler er "Mottak", "Plukk", "Flytting", "Telling" og "Justering". Disse kodene hjelper med å spore og analysere vareflyt gjennom lageret og gir innsikt i lagerets operasjoner.')
        },
        {
            id: 'bin-grp',
            title: t('manager.modules.adminResources.wizard.binGrp.title', {}, 'Lokasjonsgrupper'),
            description: t('manager.modules.adminResources.wizard.binGrp.description', {}, 'Organiser lagerplasser i logiske grupper'),
            instructions: t('manager.modules.adminResources.wizard.binGrp.instructions', {}, 'Opprett lokasjonsgrupper for å organisere lagerplasser i logiske enheter. For eksempel kan du gruppere plasser basert på funksjon (plukk, bulk, mottak), kunde eller type varer. Grupper gjør det enklere å styre tilgang, rapportere på kapasitet og administrere lagerområder effektivt.')
        },
        {
            id: 'resource',
            title: t('manager.modules.adminResources.wizard.resource.title', {}, 'Ressurser'),
            description: t('manager.modules.adminResources.wizard.resource.description', {}, 'Registrer ansatte og utstyr'),
            instructions: t('manager.modules.adminResources.wizard.resource.instructions', {}, 'Registrer ressurser som skal jobbe i lageret. Dette inkluderer lagermedarbeidere, truckførere og annet relevant personell eller utstyr. Hver ressurs kan knyttes til spesifikke operasjoner og områder. Dette muliggjør sporing av hvem som utfører hvilke oppgaver og gir grunnlag for produktivitetsanalyser.')
        },
        {
            id: 'sorting-box',
            title: t('manager.modules.adminResources.wizard.sortingBox.title', {}, 'Sorteringsbokser'),
            description: t('manager.modules.adminResources.wizard.sortingBox.description', {}, 'Definer sorteringsbokser for plukkprosessen'),
            instructions: t('manager.modules.adminResources.wizard.sortingBox.instructions', {}, 'Opprett sorteringsboksdefinisjoner som benyttes ved sortering av plukkede varer. Angi dimensjoner og kapasitet for hver bokstype. Sorteringsbokser brukes typisk ved multi-ordre plukk hvor varer samles i bokser fordelt på ulike ordrer eller leveringsdestinasjon, noe som effektiviserer pakkeprosessen.')
        },
        {
            id: 'zone',
            title: t('manager.modules.adminResources.wizard.zone.title', {}, 'Lagersoner'),
            description: t('manager.modules.adminResources.wizard.zone.description', {}, 'Del lageret inn i soner'),
            instructions: t('manager.modules.adminResources.wizard.zone.instructions', {}, 'Opprett lagersoner for å dele lageret inn i geografiske eller funksjonelle områder. Eksempler kan være "Mottaksone", "Plukkområde A", "Høylager" eller "Fryselager". Soner brukes til å styre arbeidsflyt, optimalisere ruter og organisere lagerplasser basert på funksjon eller plassering i bygget.')
        },
        {
            id: 'bin-type',
            title: t('manager.modules.adminResources.wizard.binType.title', {}, 'Lokasjonstyper'),
            description: t('manager.modules.adminResources.wizard.binType.description', {}, 'Klassifiser lagerplasser etter type'),
            instructions: t('manager.modules.adminResources.wizard.binType.instructions', {}, 'Definer lokasjonstyper som klassifiserer hvordan lagerplasser brukes. Typiske typer er "Plukklokasjon", "Bulklokasjon", "Mottakslokasjon" eller "Stagingarea". Hver type kan ha egne regler for plukk- og plasseringsprioritet. Dette sikrer at systemet foreslår riktig type lokasjon for hver situasjon.')
        },
        {
            id: 'movement-priority',
            title: t('manager.modules.adminResources.wizard.movementPriority.title', {}, 'Bevegelseskoder, prioritet'),
            description: t('manager.modules.adminResources.wizard.movementPriority.description', {}, 'Prioriter bevegelseskoder mellom lokasjonstyper'),
            instructions: t('manager.modules.adminResources.wizard.movementPriority.instructions', {}, 'Sett opp prioriteringer som styrer rekkefølgen på bevegelseskoder mellom forskjellige lokasjonstyper. For eksempel kan flytting fra mottakslokasjon til plukklokasjon få høyere prioritet enn flytting til bulklokasjon. Dette optimaliserer ressursbruken og sikrer at kritiske operasjoner utføres først.')
        },
        {
            id: 'bin-grp-relation',
            title: t('manager.modules.adminResources.wizard.binGrpRelation.title', {}, 'Lokasjonsgrupperelasjon'),
            description: t('manager.modules.adminResources.wizard.binGrpRelation.description', {}, 'Definer relasjoner mellom lokasjonsgrupper'),
            instructions: t('manager.modules.adminResources.wizard.binGrpRelation.instructions', {}, 'Opprett relasjoner mellom lokasjonsgrupper for å definere hierarkier og avhengigheter. Dette kan brukes til å styre hvilke grupper som kan motta varer fra andre grupper, eller for å definere at en hovedgruppe består av flere undergrupper. Dette gir fleksibilitet i hvordan lageret organiseres og styres.')
        }
    ];

    const handleRowSelection = (rowId: string, selected: boolean) => {
        setSelectedRows(prev => ({
            ...prev,
            [rowId]: selected
        }));
    };

    const handleBulkSelection = (rows: SelectedRows) => {
        setSelectedRows(rows);
    };

    const handleOpenSidePanel = () => {
        if (Object.values(selectedRows).filter(Boolean).length > 0) {
            setSidePanelOpen(true);
        }
    };

    const handleCloseSidePanel = () => {
        setSidePanelOpen(false);
    };

    const handleSaveSidePanel = () => {
        setRefreshTrigger(prev => prev + 1);
        setSidePanelOpen(false);
        setSelectedRows({});
    };

    const goToNextStep = () => {
        if (currentStep < wizardSteps.length - 1) {
            setCurrentStep(currentStep + 1);
            setSelectedRows({});
            setSidePanelOpen(false);
        }
    };

    const goToPreviousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setSelectedRows({});
            setSidePanelOpen(false);
        }
    };

    const handleFinish = () => {
        onClose();
    };

    const currentStepData = wizardSteps[currentStep];
    const progressPercentage = ((currentStep + 1) / wizardSteps.length) * 100;

    const renderCurrentStepContent = () => {
        const step = wizardSteps[currentStep];

        switch (step.id) {
            case 'bin-size':
                return (
                    <BinSizeDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'priority':
                return (
                    <PriorityDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'movement-type':
                return (
                    <MovementTypeDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'bin-grp':
                return (
                    <BinGrpDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'resource':
                return (
                    <ResourceDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'sorting-box':
                return (
                    <SortingBoxDefDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'zone':
                return (
                    <ZoneDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'bin-type':
                return (
                    <BinTypeDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'movement-priority':
                return (
                    <MovementCodePriorityDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            case 'bin-grp-relation':
                return (
                    <BinGrpRelationDataTable
                        refreshTrigger={refreshTrigger}
                    />
                );
            default:
                return <div>{t('manager.modules.adminResources.wizard.unknownStep', {}, 'Ukjent steg')}</div>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Compact Wizard Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg shadow-sm">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-lg">
                                <Wand2 className="w-5 h-5" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold">{t('manager.modules.adminResources.wizard.headerTitle', {}, 'Grunndata Wizard')}</h1>
                                <p className="text-purple-100 text-sm">{t('manager.modules.adminResources.wizard.headerSubtitle', {}, 'Veiledet oppsett av lagergrunndata')}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title={t('manager.modules.adminResources.wizard.closeWizardTitle', {}, 'Lukk wizard')}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span>{t('manager.modules.adminResources.wizard.stepXofY', { '0': currentStep + 1, '1': wizardSteps.length }, `Steg ${currentStep + 1} av ${wizardSteps.length}`)}</span>
                            <span>{t('manager.modules.adminResources.wizard.percentComplete', { '0': Math.round(progressPercentage) }, `${Math.round(progressPercentage)}% fullført`)}</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2">
                            <div
                                className="bg-white h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Step Indicator */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6">
                    <div className="flex items-center overflow-x-auto py-4 space-x-4">
                        {wizardSteps.map((step, index) => {
                            const isCompleted = index < currentStep;
                            const isCurrent = index === currentStep;

                            return (
                                <div
                                    key={step.id}
                                    className="flex items-center gap-3 flex-shrink-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all ${
                                                isCompleted
                                                    ? 'bg-green-500 text-white'
                                                    : isCurrent
                                                    ? 'bg-purple-600 text-white ring-4 ring-purple-200'
                                                    : 'bg-gray-200 text-gray-500'
                                            }`}
                                        >
                                            {isCompleted ? (
                                                <CheckCircle className="w-5 h-5" />
                                            ) : (
                                                <span>{index + 1}</span>
                                            )}
                                        </div>
                                        <span
                                            className={`text-sm font-medium whitespace-nowrap ${
                                                isCurrent
                                                    ? 'text-purple-600'
                                                    : isCompleted
                                                    ? 'text-gray-700'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            {step.title}
                                        </span>
                                    </div>
                                    {index < wizardSteps.length - 1 && (
                                        <div
                                            className={`w-8 h-0.5 ${
                                                isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                            }`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Navigation Buttons - Moved to top */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                    <button
                        onClick={goToPreviousStep}
                        disabled={currentStep === 0}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all ${
                            currentStep === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        {t('common.buttons.previous', {}, 'Forrige')}
                    </button>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-5 py-2 text-gray-600 hover:text-gray-800 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {t('manager.modules.adminResources.wizard.cancelWizard', {}, 'Avbryt wizard')}
                        </button>

                        {currentStep === wizardSteps.length - 1 ? (
                            <button
                                onClick={handleFinish}
                                className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all"
                            >
                                <CheckCircle className="w-5 h-5" />
                                {t('manager.modules.adminResources.wizard.finish', {}, 'Fullfør')}
                            </button>
                        ) : (
                            <button
                                onClick={goToNextStep}
                                className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-all"
                            >
                                {t('common.buttons.next', {}, 'Neste')}
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Step Instructions */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-4 p-4">
                    <div className="bg-blue-600 text-white p-2.5 rounded-lg flex-shrink-0">
                        <PlayCircle className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {currentStepData.title}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {currentStepData.description}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowInstructions(!showInstructions)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
                    >
                        <Info className="w-4 h-4" />
                        <span className="hidden sm:inline">{showInstructions ? t('manager.modules.adminResources.wizard.hideDetails', {}, 'Skjul detaljer') : t('manager.modules.adminResources.wizard.showDetails', {}, 'Vis detaljer')}</span>
                        {showInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                </div>

                {/* Collapsible Instructions Panel */}
                {showInstructions && (
                    <div className="px-4 pb-4">
                        <div className="bg-white/70 rounded-lg p-4 border border-blue-100 ml-12">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                {currentStepData.instructions}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Step Content */}
            <div className="space-y-4">
                {renderCurrentStepContent()}
            </div>
        </div>
    );
};
